import React, { useState, useMemo, useEffect } from 'react'
import {
  useCalendarioDisponibilidad,
  useCalendarioEspacios,
  useCalendarioReservas,
  useCalendarioResumen,
  useCalendarioDiasOcupacion,
  calcularNoches,
  formatearFecha,
  getColorEstado,
} from '../../hooks/useCalendarioAdmin'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { useAuth } from '../../context/AuthContext'
import { getStatusColors } from '../../utils/statusColorMap'
import { ApiClient } from '../../services/api'
import { parseLocalDateString, formatLocalDateToString } from '../../utils/dateHelpers'
import ReservaDetailModal from '../../components/admin/ReservaDetailModal'

interface CalendarDay {
  date: Date
  isCurrentMonth: boolean
  hasReservations: boolean
  reservationsCount: number
  occupancyRate: number
  reservations?: DayReservation[]
}

interface DayReservation {
  id: string
  codigo: string
  cliente: string
  espacio: string
  estado: 'check-in' | 'ocupado' | 'check-out'
}

interface GanttReservation {
  id: string
  codigo: string
  cliente: string
  espacio: string
  espacioId: string
  fechaInicio: Date
  fechaFin: Date
  noches: number
  estado: 'confirmada' | 'pendiente' | 'completada'
  unidad_reserva?: string  // 'dia' o 'noche'
}

type ViewMode = 'calendar' | 'gantt'
type Granularity = 'month' | 'week' | 'day'

const CalendarioAdmin: React.FC = () => {
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const { tienePermiso } = useAuth()
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null)
  const [selectedDayReservation, setSelectedDayReservation] = useState<DayReservation | null>(null)
  const [selectedReservaId, setSelectedReservaId] = useState<string | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('calendar')
  const [granularity, setGranularity] = useState<Granularity>('month')
  const [filteredEspacioId, setFilteredEspacioId] = useState<string | undefined>(undefined)
  const [filteredTipoEspacioId, setFilteredTipoEspacioId] = useState<string | undefined>(undefined)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    // Usar helpers de fechas para evitar diferencias entre servidor y cliente
    const today = new Date()
    const todayStr = formatLocalDateToString(today)
    return parseLocalDateString(todayStr)
  })

  // Calcular rango de fechas según granularidad
  const getDateRange = () => {
    let desde: Date
    let hasta: Date

    if (granularity === 'month') {
      // Mes completo
      desde = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      hasta = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
    } else if (granularity === 'week') {
      // Semana (lunes a domingo)
      const day = currentDate.getDay()
      desde = new Date(currentDate)
      desde.setDate(currentDate.getDate() - day)
      hasta = new Date(desde)
      hasta.setDate(desde.getDate() + 6)
    } else {
      // Día único
      desde = new Date(currentDate)
      hasta = new Date(currentDate)
    }

    const fechaDesde = `${desde.getFullYear()}-${String(desde.getMonth() + 1).padStart(2, '0')}-${String(desde.getDate()).padStart(2, '0')}`
    const fechaHasta = `${hasta.getFullYear()}-${String(hasta.getMonth() + 1).padStart(2, '0')}-${String(hasta.getDate()).padStart(2, '0')}`

    return { fechaDesde, fechaHasta }
  }

  const { fechaDesde, fechaHasta } = getDateRange()

  // Hooks para obtener datos del backend
  const { data: espacios = [], isLoading: espaciosLoading, isError: espaciosError } = useCalendarioEspacios()

  // Extraer tipos de espacio únicos de los espacios
  const tiposEspacio = useMemo(() => {
    const tiposMap = new Map<string, { id: string; nombre: string }>()
    espacios.forEach(espacio => {
      if (espacio.tipo_espacio_id && espacio.tipo) {
        tiposMap.set(espacio.tipo_espacio_id, {
          id: espacio.tipo_espacio_id,
          nombre: espacio.tipo
        })
      }
    })
    return Array.from(tiposMap.values())
  }, [espacios])

  // Filtrar espacios por tipo seleccionado
  const espaciosFiltrados = useMemo(() => {
    if (!filteredTipoEspacioId) return espacios
    return espacios.filter(e => e.tipo_espacio_id === filteredTipoEspacioId)
  }, [espacios, filteredTipoEspacioId])
  const { data: disponibilidad, isLoading: disponibilidadLoading, isError: disponibilidadError } = useCalendarioDisponibilidad(
    fechaDesde,
    fechaHasta,
    filteredEspacioId,
    filteredTipoEspacioId
  )
  const { data: reservas = [], isLoading: reservasLoading, isError: reservasError } = useCalendarioReservas(
    fechaDesde,
    fechaHasta,
    undefined, // estado
    filteredEspacioId,
    filteredTipoEspacioId
  )
  const { data: resumen, isLoading: resumenLoading } = useCalendarioResumen(
    fechaDesde,
    fechaHasta,
    filteredEspacioId,
    filteredTipoEspacioId
  )
  const { data: diasOcupacion, isLoading: diasOcupacionLoading } = useCalendarioDiasOcupacion(
    fechaDesde,
    fechaHasta,
    filteredEspacioId,
    filteredTipoEspacioId
  )

  // Funciones para cerrar modales (necesarias antes del useEffect)
  const closeModal = () => {
    setSelectedDay(null)
    setSelectedDayReservation(null)
  }

  const closeReservationModal = () => {
    setSelectedReservaId(undefined)
    setIsModalOpen(false)
  }

  // ✅ Handler para pantalla completa
  const toggleFullscreen = () => {
    const elem = document.getElementById('calendario-container')
    if (!document.fullscreenElement && elem) {
      elem.requestFullscreen().catch(err => {
        console.error(`Error al entrar en pantalla completa: ${err.message}`)
      })
      setIsFullscreen(true)
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      }
      setIsFullscreen(false)
    }
  }

  // ✅ Detectar salida de fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // ✅ Handler para cerrar modales con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Cerrar el modal más interno primero (el de reserva si está abierto)
        if (isModalOpen) {
          closeReservationModal()
        } else if (selectedDay) {
          // Solo cerrar el modal del día si no hay reserva abierta
          closeModal()
        }
      }
    }

    // Solo agregar listener si hay algún modal abierto
    if (selectedDay || isModalOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedDay, isModalOpen])

  // Crear mapa de datos de ocupación del backend para acceso rápido
  // ✅ Filtrar solo reservas confirmadas
  const ocupacionPorFecha: Record<string, {
    num_reservas: number
    ocupacion_porcentaje: number
    reservas: any[]
  }> = useMemo(() => {
    const result: Record<string, {
      num_reservas: number
      ocupacion_porcentaje: number
      reservas: any[]
    }> = {}

    if (diasOcupacion && diasOcupacion.dias) {
      diasOcupacion.dias.forEach(dia => {
        // Filtrar solo reservas confirmadas
        const reservasConfirmadas = (dia.reservas || []).filter(res => res.estado === 'confirmada')

        result[dia.fecha] = {
          num_reservas: reservasConfirmadas.length,
          ocupacion_porcentaje: dia.ocupacion_porcentaje,
          reservas: reservasConfirmadas
        }
      })
    }

    return result
  }, [diasOcupacion])

  // Convertir reservas del backend a formato Gantt - USA LA MISMA LÓGICA QUE EL CALENDARIO
  const ganttReservations: GanttReservation[] = useMemo(() => {
    // ✅ Extraer todas las reservas de ocupacionPorFecha
    // Usar composite key (reserva_id + espacio_id) para permitir múltiples espacios por reserva
    const allReservations = new Map<string, any>()

    Object.values(ocupacionPorFecha).forEach(dayData => {
      dayData.reservas.forEach(res => {
        // ✅ Key compuesta: reserva_id + espacio_id para permitir múltiples espacios
        const compositeKey = `${res.id}__${res.espacio_id}`
        if (!allReservations.has(compositeKey)) {
          allReservations.set(compositeKey, res)
        }
      })
    })

    return Array.from(allReservations.values()).map(res => {
      const unidad = res.unidad_reserva || 'noche'
      const fechaInicio = parseLocalDateString(res.fecha_entrada || res.fecha_inicio)
      const fechaFin = parseLocalDateString(res.fecha_salida || res.fecha_fin)

      // Calcular cantidad correctamente según unidad_reserva
      const dias_diff = Math.floor((fechaFin.getTime() - fechaInicio.getTime()) / (1000 * 60 * 60 * 24))
      const cantidad = unidad === 'dia' ? dias_diff + 1 : dias_diff

      // Para reservas por DÍA, el último día está completamente ocupado
      // Así que la fecha final debe ser el día siguiente para cubrir todo ese día
      if (unidad === 'dia') {
        fechaFin.setDate(fechaFin.getDate() + 1)
      }

      return {
        id: res.id,
        codigo: res.id,  // Usar ID si no hay código
        cliente: res.cliente_nombre || 'Sin nombre',
        espacio: res.espacio_nombre,
        espacioId: res.espacio_id || '',
        fechaInicio,
        fechaFin,
        noches: cantidad,
        estado: (res.estado || 'pendiente') as any,
        unidad_reserva: unidad
      }
    })
  }, [ocupacionPorFecha])

  const espaciosUnicos = Array.from(new Set(ganttReservations.map(r => r.espacio)))

  const getDayData = (date: Date) => {
    const dayString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    const dayData = ocupacionPorFecha[dayString]
    const reservationsCount = dayData ? dayData.num_reservas : 0
    const occupancyRate = dayData ? dayData.ocupacion_porcentaje : 0
    // ✅ Usar composite key (reserva_id + espacio_id) para evitar duplicados cuando una reserva tiene múltiples espacios
    const dayReservations: DayReservation[] = (dayData?.reservas || []).map(res => ({
      id: `${res.id}__${res.espacio_id}`,  // Composite key para evitar duplicados
      codigo: res.id,
      cliente: res.cliente_nombre || 'Sin nombre',
      espacio: res.espacio_nombre,
      estado: 'ocupado'
    }))
    return { reservationsCount, occupancyRate, dayReservations }
  }

  const getDaysInMonth = (date: Date): CalendarDay[] => {
    const year = date.getFullYear()
    const month = date.getMonth()

    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days: CalendarDay[] = []

    // Días del mes anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate()
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
        hasReservations: false,
        reservationsCount: 0,
        occupancyRate: 0,
        reservations: []
      })
    }

    // Días del mes actual (con datos del backend)
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(year, month, day)
      const { reservationsCount, occupancyRate, dayReservations } = getDayData(dayDate)
      const hasReservations = reservationsCount > 0

      days.push({
        date: dayDate,
        isCurrentMonth: true,
        hasReservations,
        reservationsCount,
        occupancyRate,
        reservations: dayReservations
      })
    }

    // Días del mes siguiente
    const remainingDays = 42 - days.length // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
        hasReservations: false,
        reservationsCount: 0,
        occupancyRate: 0,
        reservations: []
      })
    }

    return days
  }

  const getDaysForGranularity = (date: Date): CalendarDay[] => {
    if (granularity === 'month') {
      return getDaysInMonth(date)
    } else if (granularity === 'week') {
      // Obtener los 7 días de la semana actual
      const day = date.getDay()
      const startOfWeek = new Date(date)
      startOfWeek.setDate(date.getDate() - day)

      const days: CalendarDay[] = []
      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(startOfWeek)
        dayDate.setDate(startOfWeek.getDate() + i)
        const { reservationsCount, occupancyRate, dayReservations } = getDayData(dayDate)
        const hasReservations = reservationsCount > 0

        days.push({
          date: dayDate,
          isCurrentMonth: true,
          hasReservations,
          reservationsCount,
          occupancyRate,
          reservations: dayReservations
        })
      }
      return days
    } else {
      // Día único
      const { reservationsCount, occupancyRate, dayReservations } = getDayData(date)
      const hasReservations = reservationsCount > 0
      return [
        {
          date,
          isCurrentMonth: true,
          hasReservations,
          reservationsCount,
          occupancyRate,
          reservations: dayReservations
        }
      ]
    }
  }

  // Recalcular días cuando cambien diasOcupacion (reservas viene del backend en diasOcupacion)
  const days = useMemo(() => getDaysForGranularity(currentDate), [currentDate, diasOcupacion, granularity])
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ]

  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

  const previousPeriod = () => {
    const newDate = new Date(currentDate)
    if (granularity === 'month') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else if (granularity === 'week') {
      newDate.setDate(newDate.getDate() - 7)
    } else {
      newDate.setDate(newDate.getDate() - 1)
    }
    setCurrentDate(newDate)
  }

  const nextPeriod = () => {
    const newDate = new Date(currentDate)
    if (granularity === 'month') {
      newDate.setMonth(newDate.getMonth() + 1)
    } else if (granularity === 'week') {
      newDate.setDate(newDate.getDate() + 7)
    } else {
      newDate.setDate(newDate.getDate() + 1)
    }
    setCurrentDate(newDate)
  }

  const getDateRangeLabel = () => {
    const { fechaDesde, fechaHasta } = getDateRange()
    if (granularity === 'month') {
      const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    } else if (granularity === 'week') {
      return `${fechaDesde} - ${fechaHasta}`
    } else {
      return currentDate.toLocaleDateString('es-CL')
    }
  }

  const getOccupancyColor = (rate: number) => {
    // Usa variaciones monoclomáticas del color principal del tema
    // Alta ocupación (80%+): primaryDark (más oscuro = alerta)
    // Ocupación media (50-80%): primary (color normal)
    // Ocupación baja (20-50%): primaryLight (más claro = disponible)
    // Muy baja (<20%): primaryLight (disponible)

    if (rate >= 80) {
      return {
        backgroundColor: colors.primaryDark,
        color: '#FFFFFF',
        borderColor: colors.primaryDark
      }
    }
    if (rate >= 50) {
      return {
        backgroundColor: colors.primary,
        color: '#FFFFFF',
        borderColor: colors.primary
      }
    }
    // Baja ocupación (20-50%) y muy baja (<20%)
    return {
      backgroundColor: colors.primaryLight,
      color: colors.primaryDark,
      borderColor: colors.primaryLight
    }
  }

  const handleDayClick = (day: CalendarDay) => {
    if (day.isCurrentMonth && day.hasReservations) {
      setSelectedDay(day)
    }
  }

  const handleReservationClick = (reserva: GanttReservation) => {
    setSelectedReservaId(reserva.id)
    setIsModalOpen(true)
  }

  // Generar días para vista Gantt según granularidad
  const ganttDays: { date: Date; day: number }[] = useMemo(() => {
    if (granularity === 'month') {
      const daysInCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate()
      return Array.from({ length: daysInCurrentMonth }, (_, i) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1)
        return { date, day: i + 1 }
      })
    } else if (granularity === 'week') {
      const dayOfWeek = currentDate.getDay()
      const startOfWeek = new Date(currentDate)
      startOfWeek.setDate(currentDate.getDate() - dayOfWeek)

      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(startOfWeek)
        date.setDate(startOfWeek.getDate() + i)
        return { date, day: i + 1 }
      })
    } else {
      // day
      return [{ date: currentDate, day: 1 }]
    }
  }, [currentDate, granularity])

  // Calcular posición y ancho de reserva en Gantt
  const calculateGanttPosition = (reserva: GanttReservation) => {
    if (granularity === 'month') {
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()
      const firstDayOfMonth = new Date(currentYear, currentMonth, 1)
      const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0)

      // Para reservas por "noche", el último día está disponible (no ocupado)
      // Los días ocupados van desde fecha_entrada hasta fecha_salida - 1
      // Para "día", todos los días hasta fecha_salida están ocupados
      const fechaFinParaCalculo = new Date(reserva.fechaFin)
      if (reserva.unidad_reserva === 'noche') {
        // Restar 1 día: el día de salida está disponible
        fechaFinParaCalculo.setDate(fechaFinParaCalculo.getDate() - 1)
      }

      // Verificar si la reserva intersecta con el mes actual
      if (fechaFinParaCalculo < firstDayOfMonth || reserva.fechaInicio > lastDayOfMonth) {
        // Reserva no está en este mes
        return { left: -1, width: 0 }
      }

      // Calcular inicio: si la reserva comienza en un mes anterior, empieza el día 1
      const startDate = reserva.fechaInicio < firstDayOfMonth
        ? firstDayOfMonth
        : reserva.fechaInicio

      // Calcular fin: si la reserva termina en un mes posterior, termina el último día
      const endDate = fechaFinParaCalculo > lastDayOfMonth
        ? lastDayOfMonth
        : fechaFinParaCalculo

      const startDay = startDate.getDate()
      const endDay = endDate.getDate()

      // Calcular ancho correctamente
      let width = endDay - startDay + 1 // +1 para incluir ambos días
      if (width <= 0) width = 1

      return { left: startDay - 1, width }
    } else if (granularity === 'week') {
      const dayOfWeek = currentDate.getDay()
      const startOfWeek = new Date(currentDate)
      startOfWeek.setDate(currentDate.getDate() - dayOfWeek)

      const startDiff = Math.floor((reserva.fechaInicio.getTime() - startOfWeek.getTime()) / (1000 * 60 * 60 * 24))
      const endDiff = Math.floor((reserva.fechaFin.getTime() - startOfWeek.getTime()) / (1000 * 60 * 60 * 24))
      const width = Math.max(endDiff - startDiff, 1)

      return {
        left: Math.max(0, startDiff),
        width: Math.min(width, 7 - Math.max(0, startDiff))
      }
    } else {
      // day - mostrar si cae en este día
      const isSameDay = reserva.fechaInicio.toDateString() === currentDate.toDateString() ||
                        (reserva.fechaInicio < currentDate && reserva.fechaFin >= currentDate)
      return isSameDay ? { left: 0, width: 1 } : { left: -1, width: 0 }
    }
  }

  const getEstadoColor = (estado: string) => {
    // Map reservation estado to status types for color mapping
    let statusType: 'confirmed' | 'pending' | 'completed' | 'pending' = 'pending'

    switch (estado) {
      case 'confirmada':
        statusType = 'confirmed'
        break
      case 'completada':
        statusType = 'completed'
        break
      case 'pendiente':
        statusType = 'pending'
        break
    }

    const statusColors = getStatusColors(statusType, colors)
    return statusColors.backgroundColor
  }

  const getEstadoBadgeColor = (estado: string) => {
    // Map day reservation estado to colors
    let statusType: 'confirmed' | 'available' | 'pending' = 'pending'

    switch (estado) {
      case 'check-in':
        statusType = 'pending'
        break
      case 'ocupado':
        statusType = 'available'
        break
      case 'check-out':
        statusType = 'confirmed'
        break
    }

    const statusColors = getStatusColors(statusType, colors)
    return {
      backgroundColor: statusColors.backgroundColor,
      color: statusColors.textColor,
      borderColor: statusColors.borderColor
    }
  }

  // Usar resumen del backend (ya calculado)
  const stats = useMemo(() => {
    if (resumen) {
      return {
        ocupacionPromedio: resumen.ocupacion_promedio,
        espaciosOcupados: resumen.espacios_ocupados,
        espaciosDisponibles: resumen.espacios_disponibles,
        reservasEsteMes: resumen.reservas_este_mes
      }
    }

    // Fallback si no hay datos del backend (no debería ocurrir normalmente)
    return {
      ocupacionPromedio: 0,
      espaciosOcupados: 0,
      espaciosDisponibles: 0,
      reservasEsteMes: 0
    }
  }, [resumen])

  // Guard de permisos - Solo usuarios con reservas.ver pueden acceder
  if (!tienePermiso('reservas.ver')) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Acceso Denegado</h3>
          <p className="text-gray-600">No tienes permisos para acceder al calendario de ocupación</p>
        </div>
      </div>
    )
  }

  return (
    <div id="calendario-container" className={`min-h-screen bg-gray-50 ${isFullscreen ? 'p-0' : ''}`}>
      <div className={`${isFullscreen ? 'w-full h-screen p-4 sm:p-6 overflow-auto' : 'w-full mx-auto py-4 sm:py-8 px-3 sm:px-4 lg:px-8 max-w-7xl'}`}>
        {/* Header */}
        <div className={`mb-6 sm:mb-8 ${isFullscreen ? 'hidden' : ''}`}>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Calendario de Ocupación
          </h1>
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 flex-wrap gap-2">
            {disponibilidadLoading || reservasLoading || espaciosLoading || resumenLoading || diasOcupacionLoading ? (
              <>
                <span
                  className="px-2 py-1 rounded-md font-medium"
                  style={{
                    backgroundColor: `${colors.primary}15`,
                    color: colors.primary
                  }}
                >
                  ⏳ Cargando datos...
                </span>
              </>
            ) : disponibilidadError || reservasError || espaciosError ? (
              <>
                <span
                  className="px-2 py-1 rounded-md font-medium"
                  style={{
                    backgroundColor: `${colors.error}15`,
                    color: colors.error
                  }}
                >
                  ⚠️ Error al cargar datos
                </span>
              </>
            ) : (
              <>
                <span
                  className="px-2 py-1 rounded-md font-medium"
                  style={{
                    backgroundColor: `${colors.success}15`,
                    color: colors.success
                  }}
                >
                  ✓ Datos en tiempo real
                </span>
              </>
            )}
            <span>• {ganttReservations.length} reservas confirmadas • {espacios.length} espacios</span>
          </div>
        </div>

        {/* Stats */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 ${isFullscreen ? 'hidden' : ''}`}>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Ocupación Promedio</p>
                <p className="text-3xl font-bold" style={{ color: colors.primary }}>{stats.ocupacionPromedio}%</p>
              </div>
              <div className="text-4xl flex-shrink-0 mt-1">📊</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Unidades Ocupadas</p>
                <p className="text-3xl font-bold" style={{ color: colors.error }}>
                  {stats.espaciosOcupados}
                </p>
              </div>
              <div className="text-4xl flex-shrink-0 mt-1">🔴</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Unidades Disponibles</p>
                <p className="text-3xl font-bold" style={{ color: colors.success }}>
                  {stats.espaciosDisponibles}
                </p>
              </div>
              <div className="text-4xl flex-shrink-0 mt-1">🟢</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Reservas</p>
                <p className="text-3xl font-bold" style={{ color: colors.primary }}>
                  {stats.reservasEsteMes}
                </p>
              </div>
              <div className="text-4xl flex-shrink-0 mt-1">📅</div>
            </div>
          </div>
        </div>

        {/* Toggle View Mode */}
        <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-6 ${isFullscreen ? 'hidden' : ''}`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              {/* Granularity Selector */}
              <div className="flex items-center space-x-2 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => setGranularity('month')}
                  className="px-3 py-1 rounded text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100"
                  style={granularity === 'month' ? { backgroundColor: colors.primary, color: 'white' } : {}}
                >
                  Mes
                </button>
                <button
                  onClick={() => setGranularity('week')}
                  className="px-3 py-1 rounded text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100"
                  style={granularity === 'week' ? { backgroundColor: colors.primary, color: 'white' } : {}}
                >
                  Semana
                </button>
                <button
                  onClick={() => setGranularity('day')}
                  className="px-3 py-1 rounded text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100"
                  style={granularity === 'day' ? { backgroundColor: colors.primary, color: 'white' } : {}}
                >
                  Día
                </button>
              </div>

              {/* Date Picker */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-2 bg-white">
                <button
                  onClick={previousPeriod}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Período anterior"
                >
                  ←
                </button>
                <div className="flex items-center gap-2 px-2">
                  {granularity === 'month' && (
                    <>
                      <select
                        value={currentDate.getMonth()}
                        onChange={(e) => {
                          const newDate = new Date(currentDate)
                          newDate.setMonth(parseInt(e.target.value))
                          setCurrentDate(newDate)
                        }}
                        className="text-sm font-medium border-none bg-transparent"
                      >
                        <option value="0">Enero</option>
                        <option value="1">Febrero</option>
                        <option value="2">Marzo</option>
                        <option value="3">Abril</option>
                        <option value="4">Mayo</option>
                        <option value="5">Junio</option>
                        <option value="6">Julio</option>
                        <option value="7">Agosto</option>
                        <option value="8">Septiembre</option>
                        <option value="9">Octubre</option>
                        <option value="10">Noviembre</option>
                        <option value="11">Diciembre</option>
                      </select>
                      <select
                        value={currentDate.getFullYear()}
                        onChange={(e) => {
                          const newDate = new Date(currentDate)
                          newDate.setFullYear(parseInt(e.target.value))
                          setCurrentDate(newDate)
                        }}
                        className="text-sm font-medium border-none bg-transparent"
                      >
                        {Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - 5 + i).map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </>
                  )}
                  {granularity === 'week' && (
                    <span className="text-sm font-medium text-gray-700">{getDateRangeLabel()}</span>
                  )}
                  {granularity === 'day' && (
                    <input
                      type="date"
                      value={`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`}
                      onChange={(e) => setCurrentDate(parseLocalDateString(e.target.value))}
                      className="text-sm font-medium border-none bg-transparent"
                    />
                  )}
                </div>
                <button
                  onClick={nextPeriod}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Período siguiente"
                >
                  →
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
              {/* View Mode Icons - Solo Calendario y Gantt */}
              <div className="flex items-center space-x-1 border border-gray-300 rounded-lg p-1 w-full sm:w-auto">
                <button
                  onClick={() => setViewMode('calendar')}
                  className="flex-1 sm:flex-none p-2 rounded transition-colors text-gray-700 hover:bg-gray-100"
                  style={viewMode === 'calendar' ? { backgroundColor: colors.primary, color: 'white' } : {}}
                  title="Vista Calendario"
                >
                  📅
                </button>
                <button
                  onClick={() => setViewMode('gantt')}
                  className="flex-1 sm:flex-none p-2 rounded transition-colors text-gray-700 hover:bg-gray-100"
                  style={viewMode === 'gantt' ? { backgroundColor: colors.primary, color: 'white' } : {}}
                  title="Vista Gantt"
                >
                  📊
                </button>
              </div>

              {/* Fullscreen Button */}
              <button
                onClick={toggleFullscreen}
                className="flex-1 sm:flex-none p-2 rounded transition-colors text-gray-700 hover:bg-gray-100"
                style={isFullscreen ? { backgroundColor: colors.primary, color: 'white' } : {}}
                title={isFullscreen ? 'Salir de pantalla completa' : 'Ver a pantalla completa'}
              >
                {isFullscreen ? '🗜️' : '⛶'}
              </button>

              {/* Filtro por Tipo de Espacio */}
              <select
                value={filteredTipoEspacioId || ''}
                onChange={(e) => {
                  const newTipo = e.target.value || undefined
                  setFilteredTipoEspacioId(newTipo)
                  // Al cambiar tipo, resetear filtro de espacio individual
                  if (newTipo) {
                    setFilteredEspacioId(undefined)
                  }
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:ring-2 focus:border-transparent"
                style={{ borderColor: '#d1d5db', focusRingColor: colors.primary }}
              >
                <option value="">Todos los tipos</option>
                {tiposEspacio.map((tipo) => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nombre}
                  </option>
                ))}
              </select>

              {/* Filtro por Espacio Individual */}
              <select
                value={filteredEspacioId || ''}
                onChange={(e) => {
                  const newEspacio = e.target.value || undefined
                  setFilteredEspacioId(newEspacio)
                  // Al seleccionar espacio individual, resetear filtro de tipo
                  if (newEspacio) {
                    setFilteredTipoEspacioId(undefined)
                  }
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:ring-2 focus:border-transparent"
                style={{ borderColor: '#d1d5db', focusRingColor: colors.primary }}
              >
                <option value="">Todos los espacios</option>
                {espaciosFiltrados.map((espacio) => (
                  <option key={espacio.id} value={espacio.id}>
                    {espacio.nombre || espacio.codigo || `Espacio ${espacio.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {(disponibilidadLoading || reservasLoading || espaciosLoading) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex items-center justify-center space-x-4">
              <div className="animate-spin h-8 w-8 border-2 border-t-transparent rounded-full" style={{
                borderColor: colors.primary,
                borderTopColor: 'transparent'
              }}></div>
              <p className="text-lg text-gray-600">Cargando datos del calendario...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {(disponibilidadError || reservasError || espaciosError) && (
          <div
            className="rounded-lg shadow-sm border p-6 mb-6"
            style={{
              backgroundColor: `${colors.error}15`,
              borderColor: colors.error
            }}
          >
            <p style={{ color: colors.error }} className="font-medium">
              ⚠️ Error al cargar los datos del calendario. Por favor intenta nuevamente.
            </p>
          </div>
        )}

        {/* Calendar View */}
        {viewMode === 'calendar' && !disponibilidadLoading && !reservasLoading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Week Days - solo mostrar para month y week */}
            {granularity !== 'day' && (
              <div className={`grid ${granularity === 'month' ? 'grid-cols-7' : 'grid-cols-7'} bg-gray-100 border-b border-gray-200`}>
                {weekDays.map((day) => (
                  <div
                    key={day}
                    className="px-2 py-3 text-center text-sm font-semibold text-gray-700"
                  >
                    {day}
                  </div>
                ))}
              </div>
            )}

            {/* Calendar Days */}
            <div className={`grid ${
              granularity === 'month' ? 'grid-cols-7' :
              granularity === 'week' ? 'grid-cols-7' :
              'grid-cols-1'
            } ${granularity === 'day' ? 'auto-rows-fr' : ''}`}>
              {days.map((day, index) => (
                <div
                  key={index}
                  onClick={() => handleDayClick(day)}
                  className={`${
                    granularity === 'day'
                      ? 'min-h-screen p-6'
                      : granularity === 'week'
                      ? 'min-h-[200px] p-3'
                      : 'min-h-[100px] p-2'
                  } border-r border-b border-gray-200 cursor-pointer ${
                    !day.isCurrentMonth ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'
                  }`}
                  style={
                    day.hasReservations && day.isCurrentMonth
                      ? { ...getOccupancyColor(day.occupancyRate), borderRadius: 0 }
                      : undefined
                  }
                >
                  <div className={`${
                    granularity === 'day'
                      ? 'text-4xl font-bold mb-6'
                      : granularity === 'week'
                      ? 'text-base font-semibold mb-2'
                      : 'text-sm font-semibold mb-1'
                  } ${
                    !day.isCurrentMonth ? 'text-gray-400' : 'text-gray-900'
                  }`}>
                    {granularity === 'day'
                      ? day.date.toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                      : granularity === 'week'
                      ? day.date.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric' })
                      : day.date.getDate()
                    }
                  </div>

                  {day.hasReservations && day.isCurrentMonth && (
                    <div className={`${
                      granularity === 'day' ? 'space-y-4' :
                      granularity === 'week' ? 'space-y-2' :
                      'space-y-1'
                    }`}>
                      <div className={`${
                        granularity === 'day' ? 'text-lg font-medium' :
                        granularity === 'week' ? 'text-sm font-medium' :
                        'text-xs font-medium'
                      }`}>
                        {day.reservationsCount} espacio{day.reservationsCount !== 1 ? 's' : ''} ocupado{day.reservationsCount !== 1 ? 's' : ''}
                      </div>
                      <div className={`${
                        granularity === 'day' ? 'text-lg' :
                        granularity === 'week' ? 'text-sm' :
                        'text-xs'
                      }`}>
                        {day.occupancyRate}% ocupado
                      </div>
                      {(granularity === 'day' || granularity === 'week') && day.reservations && day.reservations.length > 0 && (
                        <div className={granularity === 'day' ? 'mt-6 space-y-3' : 'mt-3 space-y-1'}>
                          <h4 className={`font-semibold ${
                            granularity === 'day' ? 'text-base' : 'text-xs'
                          } ${day.hasReservations ? 'text-white' : 'text-gray-700'}`}>Reservas:</h4>
                          {day.reservations.map((res, idx) => (
                            <div key={idx} className={`rounded text-sm border ${
                              granularity === 'day' ? 'p-4' : 'p-2'
                            }`}
                            style={{
                              backgroundColor: '#FFFFFF',
                              borderColor: colors.primary
                            }}>
                              <p className={`font-medium text-gray-900 ${
                                granularity === 'day' ? 'text-base' : 'text-xs'
                              }`}>{res.espacio}</p>
                              <p className={`text-gray-600 ${
                                granularity === 'day' ? 'text-sm' : 'text-xs'
                              }`}>{res.cliente}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Leyenda de Ocupación:</h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-4 h-4 rounded border"
                    style={{
                      backgroundColor: colors.primaryLight,
                      borderColor: colors.primaryLight
                    }}
                  ></div>
                  <span className="text-gray-600">&lt;50% Baja</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-4 h-4 rounded border"
                    style={{
                      backgroundColor: colors.primary,
                      borderColor: colors.primary
                    }}
                  ></div>
                  <span className="text-gray-600">50-80% Media</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-4 h-4 rounded border"
                    style={{
                      backgroundColor: colors.primaryDark,
                      borderColor: colors.primaryDark
                    }}
                  ></div>
                  <span className="text-gray-600">80%+ Alta/Completa</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gantt View */}
        {viewMode === 'gantt' && !disponibilidadLoading && !reservasLoading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[70vh]">
              {/* Header with days - STICKY */}
              <div className="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
                {/* Correlativo column header */}
                <div className="w-12 flex-shrink-0 p-2 font-semibold text-gray-700 border-r border-gray-200 text-center sticky left-0 z-30 bg-gray-50">
                  #
                </div>
                {/* Espacio column header */}
                <div className="w-48 flex-shrink-0 p-4 font-semibold text-gray-700 border-r border-gray-200 sticky left-12 z-30 bg-gray-50">
                  Espacio
                </div>
                {/* Days header */}
                <div className="flex flex-1">
                  {ganttDays.map((dayObj, idx) => {
                    const dayLabel = granularity === 'month'
                      ? dayObj.day
                      : granularity === 'week'
                      ? dayObj.date.toLocaleDateString('es-CL', { weekday: 'short', month: 'numeric', day: 'numeric' })
                      : dayObj.date.toLocaleDateString('es-CL', { weekday: 'long', month: 'long', day: 'numeric' })

                    return (
                      <div
                        key={idx}
                        className={`${granularity === 'day' ? 'flex-1' : 'flex-1 min-w-[20px]'} p-1 text-center text-[10px] font-medium text-gray-600 border-r border-gray-200`}
                      >
                        {dayLabel}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Gantt rows */}
              {espaciosUnicos.map((espacio, espacioIdx) => {
                const reservasEspacio = ganttReservations.filter(r => r.espacio === espacio)

                // Calcular apilamiento de reservas que se solapan
                const getReservaRow = (reserva: GanttReservation) => {
                  let row = 0
                  for (const otherRes of reservasEspacio) {
                    if (otherRes.id === reserva.id) break
                    // Verificar si se solapan
                    if (otherRes.fechaInicio < reserva.fechaFin && otherRes.fechaFin > reserva.fechaInicio) {
                      // Si la otra reserva tiene un row asignado, usa el siguiente
                      row = Math.max(row, getReservaRow(otherRes) + 1)
                    }
                  }
                  return row
                }

                const maxRows = Math.max(0, ...reservasEspacio.map(r => getReservaRow(r))) + 1
                const rowHeight = 44 // h-10 + padding

                return (
                  <div key={espacio} className="flex border-b border-gray-200 hover:bg-gray-50">
                    {/* Correlativo column - STICKY */}
                    <div className="w-12 flex-shrink-0 p-2 font-medium text-sm text-gray-600 border-r border-gray-200 text-center sticky left-0 z-10 bg-white hover:bg-gray-50">
                      {espacioIdx + 1}
                    </div>
                    {/* Espacio column - STICKY */}
                    <div className="w-48 flex-shrink-0 p-4 font-medium text-sm text-gray-900 border-r border-gray-200 sticky left-12 z-10 bg-white hover:bg-gray-50">
                      {espacio}
                    </div>
                    {/* Gantt timeline - SCROLLABLE */}
                    <div className="flex-1 relative" style={{ minHeight: `${Math.max(60, maxRows * rowHeight + 16)}px` }}>
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex">
                        {ganttDays.map((dayObj, idx) => (
                          <div
                            key={idx}
                            className={`${granularity === 'day' ? 'flex-1' : 'flex-1 min-w-[20px]'} border-r border-gray-100`}
                          ></div>
                        ))}
                      </div>

                      {/* Reservation bars */}
                      {reservasEspacio.map((reserva) => {
                        const { left, width } = calculateGanttPosition(reserva)
                        const cellWidth = 100 / ganttDays.length
                        const row = getReservaRow(reserva)
                        const topPx = row * rowHeight + 8

                        // Filtrar reservas que no se deben mostrar (por ejemplo, en vista day con left = -1)
                        if (left < 0) return null

                        return (
                          <div
                            key={reserva.id}
                            onClick={() => handleReservationClick(reserva)}
                            className="absolute h-10 rounded px-2 py-1 text-white text-xs font-medium shadow-md hover:shadow-lg transition-shadow cursor-pointer flex items-center justify-between"
                            style={{
                              backgroundColor: getEstadoColor(reserva.estado),
                              left: `${left * cellWidth}%`,
                              width: `${width * cellWidth}%`,
                              top: `${topPx}px`
                            }}
                            title={`${reserva.codigo} - ${reserva.cliente} (${reserva.noches} ${reserva.unidad_reserva === 'dia' ? 'día(s)' : 'noche(s)'})`}
                          >
                            <span className="truncate">{reserva.codigo}</span>
                            <span className="text-[10px]">{reserva.noches}{reserva.unidad_reserva === 'dia' ? 'd' : 'n'}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Legend */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Leyenda:</h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.success }}></div>
                  <span className="text-gray-600">Confirmada</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.warning }}></div>
                  <span className="text-gray-600">Pendiente</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.primary }}></div>
                  <span className="text-gray-600">Completada</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal para detalles del día */}
        {selectedDay && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`bg-white rounded-lg shadow-xl w-full max-h-[80vh] flex flex-col ${
              deviceInfo.isMobile
                ? 'max-w-full'
                : deviceInfo.isTablet
                ? 'max-w-xl'
                : 'max-w-2xl'
            }`}>
              {/* Header */}
              <div className="text-white px-6 py-4 rounded-t-lg flex items-center justify-between flex-shrink-0" style={{
                backgroundColor: colors.primary
              }}>
                <h3 className="text-xl font-bold">
                  Detalles del {selectedDay.date.getDate()} de {monthNames[selectedDay.date.getMonth()]}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${colors.primaryDark}60`}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  ✕
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6">
                  {/* Summary */}
                  <div className={`grid gap-4 mb-6 flex-shrink-0 ${
                    deviceInfo.isMobile
                      ? 'grid-cols-1'
                      : deviceInfo.isTablet
                      ? 'grid-cols-2'
                      : 'grid-cols-3'
                  }`}>
                    <div
                      className="p-4 rounded-lg border"
                      style={{
                        backgroundColor: `${colors.primary}15`,
                        borderColor: colors.primary
                      }}
                    >
                      <p className="text-sm mb-1" style={{ color: colors.primary }}>Espacios Ocupados</p>
                      <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                        {selectedDay.reservationsCount}
                      </p>
                    </div>
                    <div
                      className="p-4 rounded-lg border"
                      style={{
                        backgroundColor: `${colors.primary}15`,
                        borderColor: colors.primary
                      }}
                    >
                      <p className="text-sm mb-1" style={{ color: colors.primary }}>Ocupación</p>
                      <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                        {selectedDay.occupancyRate}%
                      </p>
                    </div>
                    <div
                      className="p-4 rounded-lg border"
                      style={getOccupancyColor(selectedDay.occupancyRate)}
                    >
                      <p className="text-sm mb-1">Estado</p>
                      <p className="text-lg font-bold">
                        {selectedDay.occupancyRate >= 80 ? 'Lleno' : selectedDay.occupancyRate >= 50 ? 'Alta' : 'Normal'}
                      </p>
                    </div>
                  </div>

                  {/* Reservations list */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Reservas del día:</h4>
                    <div className="space-y-3">
                      {selectedDay.reservations && selectedDay.reservations.length > 0 ? (
                        selectedDay.reservations.map((reserva) => (
                          <div
                            key={reserva.id}
                            onClick={() => setSelectedDayReservation(reserva)}
                            className="border rounded-lg p-4 transition-colors cursor-pointer border-gray-200 hover:bg-gray-50"
                            style={selectedDayReservation?.id === reserva.id ? {
                              borderColor: colors.primary,
                              backgroundColor: `${colors.primary}10`
                            } : {}}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold text-gray-900">{reserva.codigo}</p>
                                <p className="text-sm text-gray-600">{reserva.cliente}</p>
                              </div>
                              <span
                                className="px-2 py-1 rounded text-xs font-medium border"
                                style={getEstadoBadgeColor(reserva.estado)}
                              >
                                {reserva.estado}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <span className="mr-2">🏕️</span>
                              <span>{reserva.espacio}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4">No hay reservas registradas para este día</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions - fuera del scroll */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0 bg-white rounded-b-lg">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cerrar
                </button>
                {selectedDayReservation && (
                  <button
                    onClick={() => {
                      // ✅ Extraer el ID real del composite key (antes del __)
                      const reservaId = selectedDayReservation.id.split('__')[0]
                      handleReservationClick({
                        id: reservaId,
                        codigo: selectedDayReservation.codigo,
                        cliente: selectedDayReservation.cliente,
                        espacio: selectedDayReservation.espacio,
                        espacioId: '',
                        fechaInicio: selectedDay.date,
                        fechaFin: selectedDay.date,
                        noches: 0,
                        estado: 'confirmada'
                      })
                    }}
                    className="px-4 py-2 text-sm rounded-md font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primaryDark
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary
                    }}
                  >
                    📋 Ver Detalles Completos
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal para detalles de reserva */}
        <ReservaDetailModal
          reservaId={selectedReservaId}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedReservaId(undefined)
          }}
        />

        {/* Fullscreen Exit Button */}
        {isFullscreen && (
          <button
            onClick={toggleFullscreen}
            className="fixed top-4 right-4 z-40 p-3 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-800 transition-colors"
            title="Salir de pantalla completa (Esc)"
          >
            ⬜
          </button>
        )}
      </div>
    </div>
  )
}

export default CalendarioAdmin
